import { useState } from 'react'
import { Button, Divider, Form, Input, InputNumber, Select, Switch, Tag, Space, App } from 'antd'
import { PlusOutlined, EditOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { plantsApi } from '@/features/masters/plants/api'
import { companiesApi } from '@/features/masters/companies/api'
import type { Plant } from '@/shared/types/master'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

export default function PlantsPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [page, setPage] = useState(1)
  const [companyFilter, setCompanyFilter] = useState<string | undefined>()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<Plant | null>(null)

  const canCreate = usePermission('plant.create')
  const canUpdate = usePermission('plant.update')

  const { data, isLoading } = useQuery({
    queryKey: ['plants', page, companyFilter],
    queryFn: () => plantsApi.list({ page, page_size: 20, company_id: companyFilter }),
  })

  const { data: companiesData } = useQuery({
    queryKey: ['companies-all'],
    queryFn: () => companiesApi.list({ page: 1, page_size: 500 }),
  })

  const companyOptions = companiesData?.items.map((c) => ({ label: c.name, value: c.id })) ?? []

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldValue('is_active', true)
    setDrawerOpen(true)
  }

  const openEdit = (record: Plant) => {
    setEditing(record)
    form.setFieldsValue(record)
    setDrawerOpen(true)
  }

  const saveMutation = useMutation({
    mutationFn: (values: Omit<Plant, 'id' | 'company_name'>) =>
      editing ? plantsApi.update(editing.id, values) : plantsApi.create(values),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plants'] })
      message.success(editing ? 'Plant updated' : 'Plant created')
      setDrawerOpen(false)
    },
    onError: () => message.error('Save failed'),
  })

  const onSubmit = () => form.validateFields().then((v) => saveMutation.mutate(v))

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Company', dataIndex: 'company_name', key: 'company_name' },
    { title: 'State', dataIndex: 'state', key: 'state', render: (v?: string) => v ?? '—' },
    { title: 'Country', dataIndex: 'country', key: 'country' },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (v: boolean) => <Tag color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: Plant) =>
        canUpdate ? (
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            Edit
          </Button>
        ) : null,
    },
  ]

  return (
    <div>
      <PageHeader
        title="Plants"
        subtitle="Manage plant locations"
        extra={
          canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New Plant
            </Button>
          )
        }
      />

      <Space style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder="Filter by company"
          options={companyOptions}
          onChange={(v) => { setCompanyFilter(v); setPage(1) }}
          style={{ width: 220 }}
        />
      </Space>

      <DataTable
        rowKey="id"
        columns={columns}
        dataSource={data?.items}
        loading={isLoading}
        total={data?.total}
        page={page}
        pageSize={20}
        onPageChange={(p) => setPage(p)}
      />

      <FormDrawer
        title={editing ? 'Edit Plant' : 'New Plant'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={onSubmit}
        loading={saveMutation.isPending}
        form={form}
      >
        <Form.Item name="name" label="Plant Name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="company_id" label="Company" rules={[{ required: true }]}>
          <Select options={companyOptions} placeholder="Select company" />
        </Form.Item>
        <Form.Item name="state" label="State">
          <Input />
        </Form.Item>
        <Form.Item name="country" label="Country" rules={[{ required: true }]} initialValue="India">
          <Input />
        </Form.Item>
        <Form.Item name="is_active" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>

        {/* Sprint 0140 — Production thresholds. The Carding waste
            variance threshold drives the GM notification: if actual
            swept waste differs from predicted (Σ output × per-card
            QC %) by more than this %, GM gets a task in their tray. */}
        <Divider orientation="left">Production thresholds</Divider>
        <Form.Item
          name="carding_waste_variance_threshold_pct"
          label="Carding waste variance threshold %"
          tooltip="If supervisor's end-of-shift Carding waste differs from predicted by more than this %, GM is notified to cross-check. Industry norm 15% (tolerates humidity/count variation). Tighten to 10% for stricter monitoring, loosen to 20% for noisier processes."
          initialValue={15}
        >
          <InputNumber
            min={1}
            max={100}
            step={0.5}
            style={{ width: '100%' }}
            addonAfter="%"
          />
        </Form.Item>

        {/* PAN-for-TDS threshold. A MONTHLY worker whose annual salary
            (basic × 12) crosses this figure must have a valid PAN before
            activation (Section 192 TDS). Daily / piece-rate labour exempt. */}
        <Divider orientation="left">Statutory thresholds</Divider>
        <Form.Item
          name="pan_required_annual_salary"
          label="PAN required above annual salary"
          tooltip="A monthly worker whose annual salary (basic × 12) reaches this figure must have a valid PAN on file before activation — needed for TDS (Section 192; without PAN, TDS is 20% under 206AA). Daily and piece-rate labour are always exempt. Default ₹2,50,000 (basic exemption). Raise to ₹4,00,000 for the new-regime floor."
          initialValue={250000}
        >
          <InputNumber<number>
            min={0}
            step={10000}
            style={{ width: '100%' }}
            addonBefore="₹"
            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={(v) => Number((v ?? '').replace(/,/g, ''))}
          />
        </Form.Item>
      </FormDrawer>
    </div>
  )
}
